// SessionHeader — top-of-page bar: gradient brain logo + title + session
// badge + Clear button + (RBAC-gated) "Open PFAP AI Assistant" entry-point.
//
// Phase 3 Step 2d extraction. Pure consumer of `useDecisionEngine()` for
// `sessionId`, `loading`, `history` (used to disable the Clear button when
// nothing's been said yet); receives `onClear` callback from the page so
// the multi-effect Clear flow (state reset + sessionStorage write + toast
// + refocus) keeps living next to the rest of the submit/resume logic.
import React from 'react';
import { Brain, RotateCcw, Sparkles } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { useAuth } from '../../contexts/AuthContext';
import { PERMISSION_MODULES as PM } from '../../constants/permissionModules';

import { useDecisionEngine } from './state/DecisionEngineContext';

export const SessionHeader = ({ onClear }) => {
  const { state } = useDecisionEngine();
  const { sessionId, loading, history } = state;
  const { hasModuleAccess } = useAuth();
  const navigate = useNavigate();
  const canSeeAI = hasModuleAccess(PM.AI_ASSISTANT_VIEW, 'view');

  return (
    <div className="border-b bg-white dark:bg-gray-900 px-6 py-4 flex items-center justify-between">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
          <Brain className="h-5 w-5 text-white" />
        </div>
        <div>
          <h1 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            Decision Engine
          </h1>
          <p className="text-xs text-gray-500">
            Ask a question or type{' '}
            <code className="px-1 bg-gray-100 dark:bg-gray-800 rounded">/</code> for commands
          </p>
        </div>
      </div>
      <div className="flex items-center gap-2">
        {canSeeAI && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/intelligence/ai-assistant')}
            data-testid="open-pfap-ai-assistant-btn"
            title="Open PFAP AI Assistant (Foundation Mode)"
            className="text-emerald-700 border-emerald-300 hover:bg-emerald-50 dark:text-emerald-300 dark:border-emerald-800 dark:hover:bg-emerald-900/20"
          >
            <Sparkles className="h-3.5 w-3.5 mr-1.5" />
            Open PFAP AI Assistant
          </Button>
        )}
        <Badge variant="outline" className="text-xs" data-testid="session-badge">
          session · {sessionId.slice(-6)}
        </Badge>
        <Button
          variant="ghost"
          size="sm"
          onClick={onClear}
          disabled={loading || history.length === 0}
          data-testid="clear-session-btn"
          title="Start a fresh decision context"
        >
          <RotateCcw className="h-3.5 w-3.5 mr-1.5" />
          Clear
        </Button>
      </div>
    </div>
  );
};
